<template>
  <div>
    <FormCard
      v-model="user"
      :cancel-enabled="false"
      @formSubmit="$emit('formSubmit', $event)">
      <CardForm
        :resource="user"
        resource-name="user"
        :section-title="$t('user.personalInfo')"
        :subtitle="$t('user.personalInfoSubtitle')">
        <div class="input-wrapper">
          <div class="input-wrapper-pair row locale-reverse-row">
            <div class="input-wrapper-pair-item col-md-12 col-24">
              <TextInput
                v-model="user.first_name"
                name="user[first_name]"
                :custom-label="$t('common.firstName')"
                required />
            </div>
            <div class="input-wrapper-pair-item col-md-12 col-24">
              <TextInput
                v-model="user.last_name"
                name="user[last_name]"
                :custom-label="$t('common.lastName')"
                required />
            </div>
          </div>
          <div class="input-wrapper-pair row">
            <div class="input-wrapper-pair-item col-md-12 col-24">
              <TextInput
                v-model="user.tax_id"
                name="user[tax_id]"
                :custom-label="$t('common.tax_id')"
                :required="false"
                />
            </div>
            <div class="input-wrapper-pair-item col-md-12 col-24">
              <ItemSelect v-model="user.gender"
                          :label="$t('common.gender')"
                          :items="availablegenders"
                          name="user[gender]"
                          value-method="value"
                          method-name="name"
                          key-method="value"
                          :shortened="false"
                          />
            </div>
          </div>
          <div class="input-wrapper-pair row">
            <div class="input-wrapper-pair-item col-md-12 col-24">
              <ItemSelect
                v-model="user.country_id"
                :items="countries"
                name="user[country_id]"
                :label="$t('common.country')"
                :shortened="false" />
            </div>
            <div class="input-wrapper-pair-item col-md-12 col-24">
              <TextInput
                v-model="user.city"
                name="user[city]"
                :custom-label="$t('common.city')" />
            </div>
          </div>
          <div class="input-wrapper-pair row">
            <div class="input-wrapper-pair-item col-md-12 col-24">
              <ItemSelect v-model="user.time_zone"
                          :items="timeZones"
                          name="user[time_zone]"
                          value-method="value"
                          method-name="name"
                          key-method="value"
                          :shortened="false" />
            </div>
            <div class="input-wrapper-pair-item col-md-12 col-24">
              <ItemSelect v-model="user.locale"
                          :items="availableLocales"
                          name="user[locale]"
                          value-method="value"
                          method-name="name"
                          key-method="value"
                          :shortened="false" />
            </div>
          </div>
          <div class="input-wrapper-pair row">
            <div class="input-wrapper-pair-item col-md-12 col-24">
              <ItemSelect
                v-model="user.nationality"
                :items="nationalityOptions"
                name="user[nationality]"
                :label="$t('common.nationality')"
                value-method="value"
                method-name="name"
                key-method="value"
                :shortened="false"
              />
            </div>
          </div>
        </div>
      </CardForm>
      <div class="card-grid">
        <div class="card-grid-item col-md-12 col-24">
          <div class="card form-card">
            <div class="form-card-header px-i">
              <div class="form-card-header-title">
                <h2 class="form-card-header-title-text">
                  {{ $t('common.email') }}
                </h2>
                <h4 class="form-card-header-title-subtitle">
                  {{ $t('user.primaryEmailText') }}
                </h4>
              </div>
            </div>
            <div class="card-body form-card-body pb-0 px-i">
              <div class="form-card-body-table">
                <SortableList v-model="userEmails" :use-drag-handle="true" lock-axis="y" @input="rankEmails($event)">
                  <SortableEmail v-for="(userEmail, index) in userEmails" :key="index" :index="index" :item="userEmail" :user="user" />
                </SortableList>
              </div>
              <!-- <div class="form-card-body-controls">
                <router-link :to="{ name: 'New User Email', params: {userId: user.id}}" role="button" class="btn btn-secondary ihjoz-button">
                  {{ $t('user.addEmail') }}
                </router-link>
              </div> -->
            </div>
          </div>
        </div>
        <div class="card-grid-item col-md-12 col-24">
          <div class="card form-card">
            <div class="form-card-header px-i">
              <div class="form-card-header-title">
                <h2 class="form-card-header-title-text">
                  {{ $t('common.mobile') }}
                </h2>
                <h4 class="form-card-header-title-subtitle">
                  {{ $t('user.mobileText') }}
                </h4>
              </div>
            </div>
            <div class="card-body form-card-body pb-0 px-i">
              <div class="form-card-body-table">
                <SortableList v-model="userMobileNumbers" :use-drag-handle="true" lock-axis="y" @input="rankMobileNumbers($event)">
                  <SortableMobileNumber v-for="(userMobileNumber, index) in userMobileNumbers" :key="index" :index="index" :item="userMobileNumber" :user="user" />
                </SortableList>
              </div>
              <!-- <div class="form-card-body-controls">
                <router-link :to="{ name: 'New User Mobile Number', params: {userId: user.id}}" role="button" class="btn btn-secondary ihjoz-button">
                  {{ $t('user.addMobile') }}
                </router-link>
              </div> -->
            </div>
          </div>
        </div>
      </div>
    </FormCard>
    <DestroyDisclaimer :title="$t('user.delete.title')"
                       :label-text="$t('user.delete.text')"
                       :button-title="$t('user.delete.title')"
                       @destroyClick="$emit('clickDestroy', $event)">
      <span v-html="$t('user.delete.body')" />
    </DestroyDisclaimer>
  </div>
</template>

<script>
import FormCard from "@shared/components/UI/FormCard.vue";
import CardForm from "@shared/components/UI/CardForm.vue";
import TextInput from "@shared/components/UI/TextInput.vue";
import ItemSelect from "@shared/components/UI/ItemSelect.vue";
import DestroyDisclaimer from "@shared/components/UI/DestroyDisclaimer.vue";
import SortableEmail from "./SortableEmail.vue";
import SortableMobileNumber from "./SortableMobileNumber.vue";
import SortableList from "./SortableList.vue";
import { loadLanguageAsync } from "@locales/i18n_setup";

export default {
  name: "UserForm",

  components: {
    FormCard,
    CardForm,
    TextInput,
    ItemSelect,
    DestroyDisclaimer,
    SortableEmail,
    SortableMobileNumber,
    SortableList
  },

  props: ["user", "userEmails", "userMobileNumbers", "countries"],

  computed: {
    submitButtonName () {
      return this.user.id ? this.$t("common.update") : this.$t("common.create");
    },

    timeZones () {
      return Dashboard.timeZones;
    },

    availableLocales () {
      return Dashboard.availableLocales;
    },

    currentTenant () {
      return Dashboard.currentTenant
    },
    nationalityOptions() {
      return this.countries.map(country => ({
        value: country.alpha_2,
        name: country.name
      }));
    },

    defaultNationality() {
      if (!this.user.nationality && this.user.country_id) {
        const selectedCountry = this.countries.find(country => country.id === this.user.country_id);
        return selectedCountry ? selectedCountry.alpha_2 : null;
      }
      return this.user.nationality;
    }
  },

  watch: {
    "user.locale": function (newVal) {
      loadLanguageAsync(newVal.toLowerCase());
    },
    defaultNationality(newVal) {
      this.user.nationality = newVal;
    }
  },

  created() {
    this.user.nationality = this.defaultNationality;
  },

  methods: {
    submitForm (e) {
      this.$emit("submitForm", e);
    },

    rankEmails (e) {
      this.$emit("rankEmails", e);
    },

    rankMobileNumbers (e) {
      this.$emit("rankMobileNumbers", e);
    }
  },
  data() {
    return {
      user: {
        gender: null,
      },
      availablegenders: [
        { value: 'male', name: 'Male' },
        { value: 'female', name: 'Female' }
      ]
    };
  }
};
</script>
